import { useMemo } from 'react'
import { DEPARTMENT_COLORS, type DrillState } from './workday-dashboard-config'

interface WorkdayEmployee {
    employee_id: number
    employee_name: string
    employee_code: string
    department_id: number
    department_name: string
    total_work_days: number
    attendance_days: number
    late_days: number
    annual_leave_days: number
    remote_days: number
}

/**
 * Business logic hook for WorkDay Dashboard
 * Handles data transformation, statistics calculation, and chart data preparation
 */
export const useWorkdayDashboard = (
    data: WorkdayEmployee[] | undefined,
    drillState: DrillState
) => {
    // Calculate summary statistics
    const stats = useMemo(() => {
        if (!data || !Array.isArray(data)) {
            return {
                totalEmployees: 0,
                totalWorkDays: 0,
                totalLateDays: 0,
                avgAttendance: 0,
            }
        }

        const totalEmployees = data.length
        const totalWorkDays = data.reduce(
            (sum, emp) => sum + (emp.total_work_days || 0),
            0
        )
        const totalLateDays = data.reduce(
            (sum, emp) => sum + (emp.late_days || 0),
            0
        )
        const totalAttendanceDays = data.reduce(
            (sum, emp) => sum + (emp.attendance_days || 0),
            0
        )
        const avgAttendance =
            totalEmployees > 0
                ? (totalAttendanceDays / totalEmployees).toFixed(1)
                : 0

        return {
            totalEmployees,
            totalWorkDays: totalWorkDays.toFixed(1),
            totalLateDays,
            avgAttendance,
        }
    }, [data])

    // Group data by department
    const departmentData = useMemo(() => {
        if (!data || !Array.isArray(data)) return []

        const grouped = data.reduce(
            (acc, emp) => {
                const deptId = emp.department_id
                if (!acc[deptId]) {
                    acc[deptId] = {
                        department_id: deptId,
                        department_name: emp.department_name,
                        total_work_days: 0,
                        attendance_days: 0,
                        late_days: 0,
                        annual_leave_days: 0,
                        remote_days: 0,
                        employee_count: 0,
                    }
                }
                acc[deptId].total_work_days += emp.total_work_days || 0
                acc[deptId].attendance_days += emp.attendance_days || 0
                acc[deptId].late_days += emp.late_days || 0
                acc[deptId].annual_leave_days += emp.annual_leave_days || 0
                acc[deptId].remote_days += emp.remote_days || 0
                acc[deptId].employee_count += 1
                return acc
            },
            {} as Record<number, any>
        )

        return Object.values(grouped)
    }, [data])

    // Prepare chart data based on drill level
    const chartData = useMemo(() => {
        if (!data || !Array.isArray(data)) return []

        if (drillState.level === 'company') {
            // Company level - show departments
            return departmentData.map((dept, index) => ({
                id: dept.department_id,
                name: dept.department_name,
                total_work_days: Number(dept.total_work_days.toFixed(1)),
                attendance: dept.attendance_days,
                late: dept.late_days,
                leave: dept.annual_leave_days,
                remote: dept.remote_days,
                employees: dept.employee_count,
                color: DEPARTMENT_COLORS[index % DEPARTMENT_COLORS.length],
            }))
        }

        if (drillState.level === 'department' && drillState.departmentId) {
            // Department level - show employees in selected department
            return data
                .filter((emp) => emp.department_id === drillState.departmentId)
                .map((emp) => ({
                    id: emp.employee_id,
                    name:
                        emp.employee_name.split(' ').slice(-2).join(' ') ||
                        emp.employee_code,
                    fullName: emp.employee_name,
                    total_work_days: emp.total_work_days,
                    attendance: emp.attendance_days,
                    late: emp.late_days,
                    leave: emp.annual_leave_days,
                    remote: emp.remote_days,
                }))
        }

        return []
    }, [data, departmentData, drillState])

    return {
        stats,
        departmentData,
        chartData,
    }
}
